// REUNIÃO GERAL (2026-09-03) — a reunião de sexta 14h com TODO o escritório.
//
// Entry do módulo: lista das reuniões → detalhe, por ESTADO interno
// (selecionadaId), sem rota nova de react-router — mesmo padrão da Weekly.
// Deep-link do sininho: sessionStorage 'op-reuniao-geral' com o id.
//
// Esta tela é aberta a TODA a empresa (`groups: []` no menu + a permissão
// operacional.reuniao_geral.ver em todos os perfis). Quem só tem `.ver` vê
// apenas pauta PUBLICADA — o filtro mora no SQL do backend, não aqui.
//
// Backend: actions op_pauta_* via POST /comercial/propostas-geradas
// (handlers/pautaSemanal.js no comercial-engine).

import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, Card, Empty, Space, Spin, Table, Tag, Typography,
} from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Se } from '../components/Se';
import { usePerms } from '../hooks/usePerms';
import PautaDetalhe from './operacional-reuniao-geral/PautaDetalhe';
import ModalConfig from './operacional-reuniao-geral/ModalConfig';
import { api, erroMsg, fmtData, fmtSemana, nomeDoEmail, num } from './operacional-reuniao-geral/utils';
import {
  PautaResumo, PERM_GERENCIAR, STATUS_REUNIAO,
} from './operacional-reuniao-geral/types';

const { Text, Title, Paragraph } = Typography;

export default function OperacionalReuniaoGeral() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const [lista, setLista] = useState<PautaResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  // Sonda de admin: config é gate por GRUPO Cognito, não por permissão da
  // matriz — não dá para perguntar ao `pode()`. Mesmo degrau da Weekly:
  // chama a action e liga a engrenagem se não vier 403.
  const [ehAdmin, setEhAdmin] = useState(false);
  const [configAberta, setConfigAberta] = useState(false);

  const podeGerenciar = pode(PERM_GERENCIAR);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_pauta_listar', {});
      setLista(Array.isArray(d?.reunioes) ? d.reunioes : []);
    } catch (e: any) {
      message.error('Falha ao carregar as reuniões: ' + erroMsg(e));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    let vivo = true;
    api('op_pauta_config_get', {})
      .then(() => { if (vivo) setEhAdmin(true); })
      .catch(() => { /* 403 = não é admin; a engrenagem simplesmente não aparece */ });
    return () => { vivo = false; };
  }, []);

  // Deep-link do sininho: consome a chave no mount e abre a reunião direto.
  useEffect(() => {
    const alvo = sessionStorage.getItem('op-reuniao-geral');
    if (alvo) {
      sessionStorage.removeItem('op-reuniao-geral');
      setSelecionadaId(alvo);
    }
  }, []);

  async function criar() {
    setCriando(true);
    try {
      const d = await api('op_pauta_criar', {});
      if (d?.id) {
        if (d.ja_existia) message.info('Já existia uma reunião desta semana — abrindo.');
        await carregar();
        setSelecionadaId(d.id);
      }
    } catch (e: any) {
      message.error('Falha ao criar: ' + erroMsg(e));
    } finally { setCriando(false); }
  }

  if (selecionadaId) {
    return (
      <div style={{ padding: 16 }}>
        <PautaDetalhe
          id={selecionadaId}
          onVoltar={() => { setSelecionadaId(null); carregar(); }}
          onMudouLista={carregar}
        />
      </div>
    );
  }

  const colunas = [
    {
      title: 'Reunião', key: 'titulo',
      render: (_: any, r: PautaResumo) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0, height: 'auto', textAlign: 'left' }}
            onClick={() => setSelecionadaId(r.id)}>
            <Text strong>{r.titulo}</Text>
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fmtData(r.data_reuniao)} · semana {fmtSemana(r.semana_ref)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Tema técnico', key: 'tema', responsive: ['md' as const],
      render: (_: any, r: PautaResumo) => (
        <Space direction="vertical" size={0}>
          <Text>{r.tema_tecnico || <Text type="secondary">a definir</Text>}</Text>
          {r.apresentador_email && (
            <Text type="secondary" style={{ fontSize: 12 }}>{nomeDoEmail(r.apresentador_email)}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Situação', key: 'situacao', width: 190,
      render: (_: any, r: PautaResumo) => {
        const st = STATUS_REUNIAO[r.status] || { label: r.status, cor: 'default' };
        return (
          <Space size={4} wrap>
            {r.publicada_em
              ? <Tag color="green">publicada</Tag>
              : <Tag color="orange">rascunho</Tag>}
            <Tag color={st.cor}>{st.label}</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Ata', key: 'ata', width: 90, responsive: ['sm' as const],
      render: (_: any, r: PautaResumo) => (r.tem_ata
        ? <Tag color="green">escrita</Tag>
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Combinados', key: 'comb', width: 130, responsive: ['sm' as const],
      render: (_: any, r: PautaResumo) => {
        const total = num(r.n_encaminhamentos);
        if (!total) return <Text type="secondary">—</Text>;
        return <Text>{num(r.n_convertidos)}/{total} viraram tarefa</Text>;
      },
    },
    {
      title: 'Material', key: 'mat', width: 100, responsive: ['lg' as const],
      render: (_: any, r: PautaResumo) => {
        const n = num(r.n_anexos);
        return n ? <Text>{n} arquivo(s)</Text> : <Text type="secondary">—</Text>;
      },
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }} align="start">
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ margin: 0 }}>Reunião Geral</Title>
            <Text type="secondary">Toda sexta, 14h · 1 hora · escritório inteiro</Text>
          </Space>
          <Space wrap>
            {ehAdmin && (
              <Button icon={<SettingOutlined />} onClick={() => setConfigAberta(true)}>
                Configuração
              </Button>
            )}
            <Se permissao={PERM_GERENCIAR}>
              <Button type="primary" icon={<PlusOutlined />} loading={criando} onClick={criar}>
                Criar a desta semana
              </Button>
            </Se>
          </Space>
        </Space>

        <Card size="small">
          <Paragraph style={{ marginBottom: 0 }}>
            A pauta é montada pelo robô toda <Text strong>segunda de manhã</Text> com o que está
            no sistema, revisada e publicada, e a reunião roda em cima dela. Cada combinado da
            sexta vira <Text strong>tarefa com dono e prazo</Text>.
          </Paragraph>
          {!podeGerenciar && (
            <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
              Você vê as pautas publicadas, baixa o material e acompanha os combinados. Para
              propor um item de pauta, fale com o Lucas ou traga na própria reunião.
            </Paragraph>
          )}
        </Card>

        {loading && !lista.length
          ? <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
          : lista.length === 0
            ? <Empty description={podeGerenciar
                ? 'Nenhuma reunião ainda. O robô cria a primeira na segunda de manhã — ou crie agora.'
                : 'Nenhuma pauta publicada ainda.'} />
            : <Table<PautaResumo> rowKey="id" size="middle" columns={colunas} dataSource={lista}
                loading={loading} pagination={{ pageSize: 20, hideOnSinglePage: true }}
                tableLayout="fixed" />}
      </Space>

      <ModalConfig aberto={configAberta} onFechar={() => setConfigAberta(false)} />
    </div>
  );
}
